// Wallboxes versus charging points (CWA-154 finding 1).
//
// One wallbox can provide several charging points. eRound registers the WALLBOX
// as one station carrying one technical name and one OCPP connection, with the
// charging points hanging beneath it — so a two-socket wallbox is one station
// with two charging points, not two stations. Registering it as two stations
// sent the customer two sets of access data for one box.
//
// The two numbers drive different decisions: how many stations to register and
// how many sets of access data to expect, versus how much charging capacity the
// customer has. So they are presented as two facts, not one.

interface CountSource {
  charge_point_count?: number;
  station_count?: number;
  station_evse_counts?: number[];
  // Only the detail response has this; it is the next best station count when
  // station_count is absent (contract §5 divergence 1).
  technical_names?: string[];
  needs_multi_station_clarification?: boolean;
}

export interface StationCounts {
  stations: number;
  chargePoints: number;
  /** Charging points per wallbox — null unless present AND coherent. */
  breakdown: number[] | null;
}

const usable = (value?: number): boolean =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

export const stationCounts = (source: CountSource): StationCounts => {
  const chargePoints = usable(source?.charge_point_count)
    ? (source.charge_point_count as number)
    : 0;

  // Absent station_count is read the way the BACKEND reads a legacy
  // charge_point_count-only submission: one single-socket station per charging
  // point. Agreeing with it beats inventing a second reading.
  const stations = usable(source?.station_count)
    ? (source.station_count as number)
    : source?.technical_names?.length || chargePoints;

  const raw = source?.station_evse_counts;
  // A breakdown is trusted only when it is coherent with the recorded total,
  // which is the value the downstream integrations use (spec assumption 9).
  const coherent =
    Array.isArray(raw) &&
    raw.length > 0 &&
    raw.every((n) => usable(n)) &&
    raw.reduce((sum, n) => sum + n, 0) === chargePoints;

  return { stations, chargePoints, breakdown: coherent ? raw : null };
};

const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

/**
 * The counts as one scannable phrase. The charging-point total is stated only
 * when it differs from the station count — for the common single-socket case
 * repeating the same number twice reads as a mistake.
 */
export const stationCountLabel = (counts: StationCounts): string => {
  const stations = plural(counts.stations, "station", "stations");
  if (!counts.chargePoints || counts.chargePoints === counts.stations) {
    return stations;
  }
  return `${stations} · ${plural(
    counts.chargePoints,
    "charging point",
    "charging points"
  )}`;
};

/** Charging points per wallbox, or null when there is nothing coherent to show. */
export const breakdownLabel = (counts: StationCounts): string | null => {
  if (!counts.breakdown) return null;
  // A breakdown that says the same thing as the totals is noise.
  if (counts.breakdown.every((n) => n === 1)) return null;
  return counts.breakdown
    .map((n, i) => `Wallbox ${i + 1}: ${plural(n, "charging point", "charging points")}`)
    .join(" · ");
};

/**
 * Whether ops has to look at this order by hand.
 *
 * Now keyed on WALLBOXES: a single multi-socket wallbox needs no clarification,
 * which is the whole point of finding 1. The backend flag is still honoured when
 * set, since it is the backend's own judgement.
 */
export const needsWallboxClarification = (source: CountSource): boolean => {
  if (source?.needs_multi_station_clarification) return true;
  return stationCounts(source).stations > 1;
};
